import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'

export type EnrollStartMode = 'now' | 'scheduled'

interface EnrollStartFieldProps {
  mode: EnrollStartMode
  onModeChange: (mode: EnrollStartMode) => void
  value: string
  onValueChange: (value: string) => void
  timezone: string
}

/**
 * Radio choice between enrolling immediately (default) and scheduling a
 * start date/time. The picker works in the sequence's timezone (shown as a
 * hint); callers convert the wall-clock value to UTC on submit via
 * scheduledStartToUtc().
 */
export function EnrollStartField({
  mode,
  onModeChange,
  value,
  onValueChange,
  timezone,
}: EnrollStartFieldProps) {
  const { t } = useTranslation('common')
  const radioName = useId()

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="radio"
          name={radioName}
          checked={mode === 'now'}
          onChange={() => onModeChange('now')}
        />
        {t('enrollStart.startNow')}
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="radio"
          name={radioName}
          checked={mode === 'scheduled'}
          onChange={() => onModeChange('scheduled')}
        />
        {t('enrollStart.startScheduled')}
      </label>
      {mode === 'scheduled' && (
        <div className="ml-6 space-y-1">
          <Input
            type="datetime-local"
            aria-label={t('enrollStart.startAtLabel')}
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {t('enrollStart.timezoneHint', { tz: timezone })}
          </p>
        </div>
      )}
    </div>
  )
}
